/**
 * Custom hook for Dashboard data logic — powered by TanStack Query
 *
 * Replaces manual useState/useEffect/useRef loading pattern with
 * useQuery for automatic caching, deduplication, retry, and
 * stale-while-revalidate.
 *
 * The three WS fetch functions already return Promises, so they
 * plug directly into queryFn with zero changes.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useCognito } from '../contexts/CognitoProvider';
import { useWebSocket } from '../contexts/WebSocketProvider';
import { fetchCDDRecords, fetchDashboardData, fetchInvitations } from '../services/configService';
import useCddRecordsStore from '../stores/cddRecordsStore';
import useInvitationsStore from '../stores/invitationsStore';

// ── Query keys (stable references for cache management) ─────────────────────
export const dashboardKeys = {
  all: ['dashboard'] as const,
  data: ['dashboard', 'data'] as const,
  cddRecords: ['dashboard', 'cddRecords'] as const,
  invitations: ['dashboard', 'invitations'] as const,
};

export interface DashboardState {
  dashboardData: any | null;
  cddRecords: any[];
  dataSource: 'real' | null;
  isLoading: boolean;
  connectionError: string | null;
}

export interface DashboardStats {
  cddTotal: number;
  cddCompleted: number;
  cddPending: number;
  cddFailed: number;
  cddHighRisk: number;
  cddRecent: any[];
}

export const useDashboardData = (): [DashboardState, DashboardStats] => {
  const { isAuthenticated } = useCognito();
  const { isAuthenticated: wsAuthenticated } = useWebSocket();

  const isReady = isAuthenticated && wsAuthenticated;

  // Zustand store setters (still used so real-time WS pushes stay in sync)
  const setStoreRecords = useCddRecordsStore((s) => s.setRecords);
  const setStoreInvitations = useInvitationsStore((s) => s.setInvitations);
  const clearInvitations = useInvitationsStore((s) => s.clearInvitations);
  const cddRecords = useCddRecordsStore((s) => s.allRecords);

  // ── Queries ──────────────────────────────────────────────────────────────
  const dashboardQuery = useQuery({
    queryKey: dashboardKeys.data,
    queryFn: fetchDashboardData,
    enabled: isReady,
  });

  const cddQuery = useQuery({
    queryKey: dashboardKeys.cddRecords,
    queryFn: fetchCDDRecords,
    enabled: isReady,
  });

  const invitationsQuery = useQuery({
    queryKey: dashboardKeys.invitations,
    queryFn: fetchInvitations,
    enabled: isReady,
  });

  // ── Sync query results into Zustand stores (for real-time push compat) ──
  useEffect(() => {
    if (cddQuery.data) {
      setStoreRecords(cddQuery.data.data);
    }
  }, [cddQuery.data, setStoreRecords]);

  useEffect(() => {
    if (invitationsQuery.data) {
      setStoreInvitations(invitationsQuery.data.data);
    }
  }, [invitationsQuery.data, setStoreInvitations]);

  // Clear store on logout
  useEffect(() => {
    if (!isAuthenticated) {
      clearInvitations();
    }
  }, [isAuthenticated, clearInvitations]);

  // ── Derive loading / error ───────────────────────────────────────────────
  const isLoading = !isReady || dashboardQuery.isLoading || cddQuery.isLoading || invitationsQuery.isLoading;
  const connectionError =
    dashboardQuery.error?.message ||
    cddQuery.error?.message ||
    invitationsQuery.error?.message ||
    null;

  // ── Compute stats from store records (unchanged) ─────────────────────────
  const cddTotal = cddRecords.length;
  const cddCompleted = cddRecords.filter(
    (r) => r.status === 'completed' || (r.status as string) === 'done',
  ).length;
  const cddPending = cddRecords.filter(
    (r) => r.status === 'pending' || r.status === 'in-progress',
  ).length;
  const cddFailed = cddRecords.filter((r) => r.status === 'failed').length;
  const cddHighRisk = cddRecords.filter((r) => r.riskLevel === 'high').length;
  const cddRecent = [...cddRecords]
    .sort((a, b) => (b.createdDate || '').localeCompare(a.createdDate || ''));

  const state: DashboardState = {
    dashboardData: dashboardQuery.data?.data ?? null,
    cddRecords,
    dataSource: dashboardQuery.data ? 'real' : null,
    isLoading,
    connectionError,
  };

  const stats: DashboardStats = {
    cddTotal,
    cddCompleted,
    cddPending,
    cddFailed,
    cddHighRisk,
    cddRecent,
  };

  return [state, stats];
};

